import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import mfaApiService from 'api/MfaApiService';
import { useSelector } from 'store/hooks';
import { secureTokenStorage } from 'utils/secureTokenStorage';
import './MfaSetup.scss';

interface MfaSetupProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const MfaSetup: React.FC<MfaSetupProps> = ({ onSuccess, onCancel }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [secretCode, setSecretCode] = useState('');
  const [qrCodeValue, setQrCodeValue] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const appState = useSelector((state) => state.app);

  const steps = ['Generate QR Code', 'Scan with Authenticator', 'Verify Code'];

  useEffect(() => {
    initiateMFASetup();
  }, []);

  const getJwtEmail = (): string | null => {
    try {
      const parsed = secureTokenStorage.get();
      if (!parsed) return null;
      const jwt: string | undefined = parsed?.id_token || parsed?.access_token;
      if (!jwt || typeof jwt !== 'string' || !jwt.includes('.')) return null;
      const payloadPart = jwt.split('.')[1];
      if (!payloadPart) return null;
      const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = window.atob(padded);
      const payload = JSON.parse(json);
      return payload?.email || payload?.['cognito:username'] || payload?.username || null;
    } catch {
      return null;
    }
  };

  const resolveUserEmail = (): string =>
    appState?.user?.mail || localStorage.getItem('user-email') || getJwtEmail() || 'user@example.com';

  const initiateMFASetup = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('MfaSetup - Initiating MFA setup...');
      if (!secureTokenStorage.get()) {
        throw new Error('No authentication token found. Please log in again.');
      }

      console.log('MfaSetup - Calling mfaApiService.setupMfa()');
      const response = await mfaApiService.setupMfa();
      console.log('MfaSetup - Setup successful, received secret code');

      setSecretCode(response.secretCode);

      // Generate QR code value in format:
      // otpauth://totp/AppName:user@email?secret=SECRET&issuer=AppName
      const userEmail = resolveUserEmail();
      const appName = 'E-Delivery';
      const qrValue =
        `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(userEmail)}` +
        `?secret=${response.secretCode}&issuer=${encodeURIComponent(appName)}`;
      setQrCodeValue(qrValue);

      setActiveStep(1);
    } catch (err: any) {
      console.error('MfaSetup - Error:', err);
      console.error('MfaSetup - Error response:', err.response);
      console.error('MfaSetup - Error response data:', JSON.stringify(err.response?.data, null, 2));

      // Try to extract the most specific error message
      let errorMessage = 'Failed to initiate MFA setup. Please try refreshing the page.';

      if (err.response?.data) {
        const { data } = err.response;
        errorMessage =
          data.message || data.error?.message || data.debug || (typeof data === 'string' ? data : JSON.stringify(data));
      } else if (err.message) {
        errorMessage = err.message;
      }

      console.error('MfaSetup - Final error message:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await mfaApiService.verifyMfa(verificationCode);
      setSuccess(true);
      setActiveStep(2);

      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  return (
    <Box className="mfa-setup-container">
      <Paper elevation={3} className="mfa-setup-paper">
        <Typography variant="h5" gutterBottom>
          Setup Two-Factor Authentication
        </Typography>

        <Stepper activeStep={activeStep} className="mfa-stepper">
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" className="mfa-alert" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="mfa-alert">
            MFA has been successfully enabled!
          </Alert>
        )}

        {loading && activeStep === 0 && (
          <Box className="mfa-loading">
            <CircularProgress />
            <Typography>Generating QR code...</Typography>
          </Box>
        )}

        {!loading && activeStep === 1 && qrCodeValue && (
          <Box className="mfa-qr-section">
            <Typography variant="body1" gutterBottom>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
            </Typography>

            <Box className="mfa-qr-code">
              <QRCodeSVG value={qrCodeValue} size={200} level="M" />
            </Box>

            <Typography variant="body2" className="mfa-manual-entry">
              Can&apos;t scan the QR code? Enter this key manually in your authenticator app:
            </Typography>
            <TextField
              value={secretCode}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              size="small"
              className="mfa-secret-code"
            />

            <Typography variant="body2" className="mfa-instructions" gutterBottom>
              Enter the 6-digit code from your authenticator app:
            </Typography>
            <TextField
              value={verificationCode}
              onChange={handleCodeChange}
              placeholder="000000"
              fullWidth
              variant="outlined"
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' },
              }}
              className="mfa-verification-input"
            />

            <Box className="mfa-actions">
              {onCancel && (
                <Button variant="outlined" onClick={onCancel} disabled={loading}>
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify & Enable'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MfaSetup;
